// Admin API transport: Clerk token, authFetch, the shared client and URL helpers (A7Y-774).

import { createApiClient } from '@a7y/api-client';

/**
 * Options a token provider understands. `skipCache` maps straight onto Clerk's
 * `getToken({ skipCache: true })` — it forces a new session JWT instead of
 * returning the one already in memory.
 */
export type TokenProviderOptions = { skipCache?: boolean };
export type TokenProvider = (opts?: TokenProviderOptions) => Promise<string | null>;

// Token provider: set by AdminAuthContext once Clerk loads
let tokenProvider: TokenProvider | null = null;

// Deduplicate concurrent getToken() calls so parallel API requests share one
// Clerk session hit instead of each triggering their own, which Clerk rate-limits.
let inflightTokenPromise: Promise<string | null> | null = null;

async function getToken(opts?: TokenProviderOptions): Promise<string | null> {
  if (!tokenProvider) return null;
  // A forced refresh must never be answered by the in-flight promise for a
  // CACHED read — that promise is very likely the stale token we are trying
  // to get away from. It also does not become the shared promise, so a
  // concurrent ordinary request is not made to wait on it.
  if (opts?.skipCache) return tokenProvider(opts);
  if (inflightTokenPromise) return inflightTokenPromise;
  inflightTokenPromise = tokenProvider().finally(() => {
    inflightTokenPromise = null;
  });
  return inflightTokenPromise;
}

export function setTokenProvider(provider: TokenProvider) {
  tokenProvider = provider;
}

/**
 * A 401 answered once with a force-refreshed token.
 *
 * Clerk session JWTs are short-lived, and `getToken()` serves the cached one
 * until its own refresh loop replaces it. A tab that was backgrounded (or a
 * laptop that slept) resumes holding a JWT that is already expired, so the
 * first request after waking is rejected — measured in the local preview on
 * 2026-09-15: `JWT is expired. Expiry date: 11:25:03, Current date: 11:39:14`,
 * with the very next poll succeeding once Clerk had caught up. Without this,
 * every admin panel on screen renders an error for one cycle.
 *
 * Scope is deliberately narrow: ONE retry, only on 401, only when a token was
 * actually sent, and only when the refreshed token differs from the one that
 * was rejected — a 401 for any other reason (not a superadmin, revoked
 * session) must surface immediately rather than be retried in a loop. This is
 * the per-call-site `token_expired` handling that `@a7y/api-client` documents
 * on `onAuthError`, which routes only hard, non-recoverable auth failures.
 *
 * Exported so `api.auth.test.ts` can assert the retry, the no-retry cases and
 * the concurrent-request dedupe directly — that test IS this fix's detector.
 */
export const authFetch: typeof fetch = async (input, init) => {
  const method = (init as RequestInit)?.method ?? 'GET';
  const url = typeof input === 'string' ? input : (input as Request).url;
  const body = (init as RequestInit)?.body;

  const send = async (token: string | null): Promise<Response> => {
    const headers = new Headers((init as RequestInit)?.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(input, { ...init, headers });
  };

  const token = await getToken();
  console.log(`[admin-api] ${method} ${url}`, body ? JSON.parse(body as string) : '');
  let response = await send(token);

  // A `Request` input has had its body consumed by the attempt above, so it
  // cannot be replayed. Every caller here passes a URL string; the guard is so
  // that stays true rather than being assumed.
  if (response.status === 401 && token && typeof input === 'string') {
    const refreshed = await getToken({ skipCache: true });
    if (refreshed && refreshed !== token) {
      response = await send(refreshed);
    }
  }

  if (!response.ok) {
    console.error(`[admin-api] ${method} ${url} → ${response.status} ${response.statusText}`);
  }
  return response;
};
// In dev, use relative URL (Vite proxy forwards to API).
// In staging/production builds, use the full API URL.
export const baseUrl = import.meta.env.DEV ? '/api/admin' : `${import.meta.env.VITE_API_URL}/admin`;

export const client = createApiClient({
  baseUrl,
  defaultHeaders: { 'Content-Type': 'application/json' },
  fetchFn: authFetch,
});

export type PaginatedResponse<T> = {
  data: T[];
  total: number;
  page?: number;
  limit?: number;
};

// A7Y-1898: a few affiliate endpoints need raw response access — the payout
// hard-cap returns 409 with a `{ requiresConfirmation, cap, amount }` body that
// drives a second confirm; link creation returns 409 on a duplicate token; the
// CSV export streams `text/csv`. The generic `client` throws on non-2xx and
// only exposes JSON, so those three go through this helper instead.
export async function rawAdminFetch(path: string, init?: RequestInit): Promise<Response> {
  return authFetch(`${baseUrl}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
}

// The shared `client` only exposes get/post/patch/delete. A handful of affiliate
// endpoints use PUT (idempotent replace of discount / coupon / campaign); route
// those through this raw JSON helper so we honour the documented verb.
export async function rawJson<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await rawAdminFetch(path, {
    method,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    throw new Error(`Request to ${path} failed: ${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Build an admin URL from an optional params bag, dropping empty values and
 * rendering `force: true` as `force=1`. Centralised so a new cached admin
 * endpoint (audit-spend, Stats → Keyword Database) cannot quietly forget the
 * force flag and become the one section that ignores the Refresh button.
 */
export function adminQueryUrl(
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v === undefined || v === false) continue;
    sp.set(k, v === true ? '1' : String(v));
  }
  const qs = sp.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Audit-spend callers keep their name; one convention builds both URL families. */
export const auditSpendUrl = adminQueryUrl;
